import type { RequestFunctionParams } from '@bigo/yapi-to-ts';
import { useAppStore } from '@/store/app';
import { getParamFromUrl } from '@/common/utils';
import { get, getPhp, postCpp, postJava, postUpload } from '@/common/xhr-axios';
import { STATUS_CODE } from '@/constants';
import { track } from '@bigo/logger';
import { JAVA_DOMAIN_MAP, CPP_DOMAIN_MAP } from './config';
import { getReplaceDomain } from '@/compositions/useGetCountryCode';

interface ApiType {
  isC?: boolean;
  isPHP?: boolean;
  isYapi?: boolean;
  isUpload?: boolean;
}

interface CustomParams extends ApiType {
  path: string;
  mockUrl?: string;
  method: string;
  data?: any;
  headers?: any;
}

type ExtendedRequestFunctionParams = RequestFunctionParams & ApiType;

type Payload = CustomParams | ExtendedRequestFunctionParams;

export const getCurrentEnv = () => {
  let CURRENT_ENV = import.meta.env.VITE_BUILD_ENV || 'dev';
  if (CURRENT_ENV === 'dev') {
    const envFromUrl = getParamFromUrl('env');
    if (envFromUrl) {
      CURRENT_ENV = envFromUrl;
    }
    const envFromLocalStorage = localStorage.getItem('__CUR_ENV__');
    if (envFromLocalStorage) {
      CURRENT_ENV = envFromLocalStorage;
    }
  }

  return CURRENT_ENV;
};

const getRequestUrl = async (payload: Payload) => {
  if (payload?.isPHP || payload?.isUpload) return payload.path;
  const DOMAIN_MAP = payload.isC ? CPP_DOMAIN_MAP : JAVA_DOMAIN_MAP;
  // 如果下面判断都没有击中，说明是自定义接口
  const urlMap = DOMAIN_MAP;
  const CURRENT_ENV = getCurrentEnv() as keyof typeof DOMAIN_MAP;
  const url =
    CURRENT_ENV === 'mock'
      ? `${payload.mockUrl || urlMap.mock}${payload.path}`
      : `${urlMap[CURRENT_ENV] || ''}${payload.path}`;
  const replaceUrl = await getReplaceDomain(url);
  return replaceUrl.replace('http://', 'https://');
};

export interface ResData<T> {
  code: number;
  data: T;
  message: any;
}

export interface FormatedData<T> {
  code: number;
  error?: ResData<T>;
  data: T;
}

export default async function request<TResponseData>(
  payload: Payload,
): Promise<FormatedData<TResponseData>> {
  // 请求地址,mock地址自动获取
  const url = await getRequestUrl(payload);
  // eslint-disable-next-line consistent-return
  return new Promise<FormatedData<TResponseData>>((resolve, reject) => {
    // 处理接口的具体逻辑
    try {
      // 这里所有请求都使用了这个格式化处理，符合大部分场景需求。
      // 在实际编码过程中，有特殊情况，可根据实际需求进行调整
      const onSuccess = (res: ResData<TResponseData>) => {
        if (res.code === STATUS_CODE.NEED_PHONE_AUTH) {
          useAppStore().setPhoneAuthCnt();
        }
        const isSuccess = res.code === STATUS_CODE.SUCCESS || res.res_code === STATUS_CODE.SUCCESS;

        // 业务状态码异常时，上报具体code
        if (!isSuccess) {
          track({
            log_action: 'API_ABNORMAL_CODE',
            log_event: String(res.code),
            log_content: url,
            extra: {
              req: JSON.stringify(payload),
              res: JSON.stringify(res),
            },
          });
        }
        if (url.includes('bigo_db_qry/GetUserInfoFromServer')) {
          return {
            // @ts-ignore
            code: res.res_code,
            // @ts-ignore
            data: res.user_info,
            error: isSuccess ? undefined : res,
          };
        }
        return {
          code: res.code,
          error: isSuccess ? undefined : res,
          data: res.data,
        };
      };

      // 具体请求逻辑
      if (payload.isUpload) {
        // @ts-ignore
        resolve(postUpload(url, payload.data, payload.headers));
      } else if (payload.isPHP) {
        resolve(getPhp(url, payload.data).then(onSuccess));
      } else if (payload.method === 'GET') {
        resolve(get(url, payload.data).then(onSuccess));
      } else if (payload.method === 'POST' && payload.isC) {
        resolve(postCpp(url, payload.data).then(onSuccess));
      } else if (payload.method === 'POST') {
        resolve(postJava(url, payload.data).then(onSuccess));
      }
    } catch (e) {
      // 错误处理，可以在这里上报错误
      return reject(e);
    }
  }).catch((e) => {
    if (e?.response?.status === STATUS_CODE.TOKEN_ERROR) {
      useAppStore().setTokenErrCnt();
    }
    return Promise.reject(e);
  });
}
